import Cookies from 'js-cookie'
import { Block } from 'components/ui'
import { t } from '@stokr/components-library'
import { PageMeta } from 'components/PageMeta'
import { BaseLayout } from 'components/layouts'
import { scrollToElement } from 'utils/scrollUtils'
import HeroBlock from 'components/ui/homeHero/HeroBlock'
import { useLocation, useNavigate } from 'react-router-dom'
import React, { Suspense, useEffect, useRef, lazy } from 'react'

const OurSolutions = lazy(() => import('components/ui/howItWorks/OurSolutions'))
const LicensesSection = lazy(() => import('components/ui/licensesSection'))
const InvestingSection = lazy(() => import('components/ui/howItWorks/InvestingSection'))
const TrendingVentures = lazy(() => import('components/ui/trendingVentures'))
const Testimonials = lazy(() => import('components/ui/testimonials'))
const GalleryVideoBlock = lazy(() => import('components/ui/galleryVideoBlock'))
const LearnMoreAboutInvesting = lazy(() => import('components/ui/learnMoreAboutInvesting'))
const NewsLetter = lazy(() => import('components/ui/newsletter'))
const AssetClasses = lazy(() => import('components/ui/asset-classes'))

// Reorder to change the sitelink emphasis hinted to Google (not a guarantee).
// `path` stays a route (not copy); `nameKey` resolves to the sitelink label.
const NAV_LINKS = [
  { nameKey: 'home.navLinks.featuredAssets', path: '/featured-assets' },
  { nameKey: 'home.navLinks.assetTokenization', path: '/solutions/asset-tokenization' },
  { nameKey: 'home.navLinks.tokenizationAsService', path: '/solutions/tokenization-as-service' },
  { nameKey: 'common.nav.team', path: '/team' },
  { nameKey: 'common.nav.careers', path: '/careers' },
]

// Built lazily (not at module top-level) so `t()` runs after initCopy()
// registers the copy source. Static `t()` at module load can still miss the
// dictionary if this file is evaluated before locales, or against a second
// `@stokr/components-library` instance.
const buildHomeStructuredData = () =>
  JSON.stringify([
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'STOKR',
      url: 'https://stokr.io',
      logo: 'https://stokr.io/stokr-logo.svg',
      sameAs: [
        'https://www.linkedin.com/company/stokr/',
        'https://www.instagram.com/stokr_media/',
        'https://twitter.com/stokr_io',
        'https://www.facebook.com/STOKR-696540447399052',
        'https://www.youtube.com/channel/UCUuQtRhbcFcU4xeTrDl3iQQ',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'STOKR',
      url: 'https://stokr.io',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Primary navigation',
      itemListElement: NAV_LINKS.map((link, index) => ({
        '@type': 'SiteNavigationElement',
        position: index + 1,
        name: t(link.nameKey),
        url: `https://stokr.io${link.path}`,
      })),
    },
  ]).replace(/</g, '\\u003c')

// The Home component is mounted for /, /solutions and /featured-assets
// (see AppRouter). Each variant has its own meta from static_OG.js.
const HomePageMeta = ({ pathname }) => {
  const structuredData = (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildHomeStructuredData() }} />
  )
  if (pathname.startsWith('/solutions')) {
    return (
      <>
        <PageMeta
          title={t('home.pageMeta.solutionsTitle')}
          image="https://res.cloudinary.com/stokr/image/upload/q_auto,f_auto/v1664901626/products/STOKR_Products_4x_uupobc_xzdjno.png"
          path="/solutions"
        />
        {structuredData}
      </>
    )
  }
  return (
    <>
      <PageMeta path="/" />
      {structuredData}
    </>
  )
}

export function Home(props) {
  const stokePostRef = useRef(null)
  const oneOOneRef = useRef(null)
  const productsRef = useRef(null)
  const location = useLocation()
  const navigate = useNavigate()
  const pendingScrollTimerRef = useRef(null)

  const checkForReferralCode = (params) => {
    const urlParams = new URLSearchParams(params)
    const referalCode = urlParams.get('ref')

    if (referalCode) {
      let inOneHour = 1 / 24

      let cookieOptions = {
        expires: inOneHour,
      }
      Cookies.set('STOKR_REFERRAL_CODE', referalCode, cookieOptions)
    }
  }

  const checkForInternalUser = (params) => {
    const urlParams = new URLSearchParams(params)

    const isInternalUser = urlParams.get('internal') === 'true'

    if (isInternalUser) {
      // Set internal_user cookie to last 1 year
      Cookies.set('internal_user', 'true', {
        expires: 365, // days
        domain: import.meta.env.VITE_COOKIE_DOMAIN, // share across subdomains
        path: '/',
      })
    }
  }

  const clearPendingScroll = () => {
    if (pendingScrollTimerRef.current) {
      clearTimeout(pendingScrollTimerRef.current)
      pendingScrollTimerRef.current = null
    }
  }

  // Sections like trendingVentures fetch their data client-side (post-hydration),
  // so the wrapper <div> mounts immediately but stays ~10px tall until the API
  // returns and content fills in. The retry loop must wait for actual content
  // (offsetHeight above a threshold), not just for the ref to exist — otherwise
  // we scroll to a position based on the empty placeholder and the page layout
  // shifts AFTER the scroll lands, leaving the user on the wrong section.
  //
  // Retries × delay budget = ~9s, enough for slow projects-fetch on
  // /featured-assets even on poor connections.
  const MIN_SECTION_HEIGHT_PX = 200
  const scheduleScrollWhenReady = (targetRef, options = {}, retriesLeft = 60, delayMs = 150) => {
    clearPendingScroll()

    const attempt = () => {
      const el = targetRef.current
      if (el && el.offsetHeight >= MIN_SECTION_HEIGHT_PX) {
        scrollToElement(targetRef, options)
        pendingScrollTimerRef.current = null
        return
      }

      if (retriesLeft <= 0) {
        // Final attempt — scroll to whatever's there rather than not scroll at all.
        if (el) scrollToElement(targetRef, options)
        pendingScrollTimerRef.current = null
        return
      }

      pendingScrollTimerRef.current = setTimeout(() => {
        scheduleScrollWhenReady(targetRef, options, retriesLeft - 1, delayMs)
      }, delayMs)
    }

    attempt()
  }

  useEffect(() => {
    const { state, search, pathname } = location

    checkForReferralCode(search)
    checkForInternalUser(search)

    // Handle returning from specific pages
    if (state?.returningFromPost) {
      scheduleScrollWhenReady(stokePostRef, { block: 'center' })
      navigate(pathname + search, { replace: true })
      return () => clearPendingScroll()
    }

    if (state?.returningFrom101s) {
      scheduleScrollWhenReady(oneOOneRef, { block: 'center' })
      navigate(pathname + search, { replace: true })
      return () => clearPendingScroll()
    }

    if (pathname.includes('/solutions')) {
      scheduleScrollWhenReady(productsRef)
    }

    return () => clearPendingScroll()
  }, [location.pathname, location.search, location.state, navigate])

  useEffect(() => {
    // Some header interactions can re-click /featured-assets while already on
    // a Home variant route, which does not change pathname and thus won't
    // retrigger the location-based effect. Capture that click and force scroll.
    const onDocumentClickCapture = (event) => {
      const anchor = event.target?.closest?.('a[href]')
      if (!anchor) return

      let targetPath = ''
      try {
        const url = new URL(anchor.getAttribute('href'), window.location.origin)
        targetPath = url.pathname
      } catch (_err) {
        return
      }

      if (targetPath === '/solutions') {
        scheduleScrollWhenReady(productsRef)
      }
    }

    document.addEventListener('click', onDocumentClickCapture, true)
    return () => {
      document.removeEventListener('click', onDocumentClickCapture, true)
    }
  }, [])

  return (
    <BaseLayout {...props}>
      <HomePageMeta pathname={location.pathname} />
      <Block compact noBorder>
        <HeroBlock />
      </Block>

      <Suspense fallback={null}>
        <Block compact noBorder customCss={{ marginBottom: '-60px' }}>
          <LicensesSection />
        </Block>

        <Block compact>
          <div id="solutions" ref={productsRef}>
            <OurSolutions />
          </div>
        </Block>

        <Block compact>
          <InvestingSection />
        </Block>

        <Block compact last>
          <AssetClasses />
        </Block>

        <Block compact>
          <TrendingVentures />
        </Block>

        <Block compact>
          <Testimonials />
        </Block>

        <Block compact>
          <div ref={stokePostRef}>
            <GalleryVideoBlock />
          </div>
        </Block>

        <Block compact>
          <NewsLetter />
        </Block>

        <Block compact>
          <div ref={oneOOneRef}>
            <LearnMoreAboutInvesting />
          </div>
        </Block>
      </Suspense>
    </BaseLayout>
  )
}

export default Home
